import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { getToken } from '@/utils/storage';

interface Plan {
  code: string;
  name: string;
  max_devices: number;
  price: number;
  features: string[];
}

// フォーム用のプラン（featuresを文字列として扱う）
interface PlanFormData {
  code: string;
  name: string;
  max_devices: number;
  price: number;
  featuresText: string;  // 入力用の文字列
}

interface EmailTemplate {
  subject: string;
  body: string;
}

interface Settings {
  require_device_auth: boolean;
  trial_days: number;
  default_plan: string;
}

interface Product {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  plans: Plan[];
  email_templates: Record<string, EmailTemplate>;
  settings: Record<string, unknown>;
  created_at: string;
}

interface FormData {
  code: string;
  name: string;
  description: string;
  plans: PlanFormData[];
  email_templates: Record<string, EmailTemplate>;
  settings: Settings;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

// デフォルト値
const defaultPlanForm: PlanFormData = { code: 'free', name: '無料プラン', max_devices: 1, price: 0, featuresText: '' };
const defaultEmailTemplates: Record<string, EmailTemplate> = {
  welcome: {
    subject: '【{product_name}】ご登録ありがとうございます',
    body: '{customer_name}様\n\nこの度は「{product_name}」をご利用いただき、誠にありがとうございます。',
  },
};
const defaultSettings: Settings = {
  require_device_auth: false,
  trial_days: 30,
  default_plan: 'free',
};

export const ProductsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    description: '',
    plans: [{ ...defaultPlanForm }],
    email_templates: { ...defaultEmailTemplates },
    settings: { ...defaultSettings },
  });

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/products`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });
      if (!res.ok) throw new Error('プロダクト一覧の取得に失敗しました');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Plan[] を PlanFormData[] に変換
  const plansToFormData = (plans: Plan[]): PlanFormData[] => {
    return plans.map(plan => ({
      code: plan.code,
      name: plan.name,
      max_devices: plan.max_devices,
      price: plan.price,
      featuresText: plan.features.join(', '),
    }));
  };

  // PlanFormData[] を Plan[] に変換
  const formDataToPlans = (plans: PlanFormData[]): Plan[] => {
    return plans.map(plan => ({
      code: plan.code,
      name: plan.name,
      max_devices: plan.max_devices,
      price: plan.price,
      features: plan.featuresText.split(',').map(f => f.trim()).filter(f => f),
    }));
  };

  const handleOpenDialog = async (product?: Product) => {
    if (product) {
      // 編集時は詳細APIから最新データを取得
      try {
        const res = await fetch(`${API_BASE}/products/${product.code}`, {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
          },
        });
        if (!res.ok) throw new Error('プロダクト詳細の取得に失敗しました');
        const fullProduct: Product = await res.json();

        setEditingProduct(fullProduct);
        // settingsを安全に変換
        const productSettings = fullProduct.settings && Object.keys(fullProduct.settings).length > 0
          ? {
              require_device_auth: Boolean(fullProduct.settings.require_device_auth ?? defaultSettings.require_device_auth),
              trial_days: Number(fullProduct.settings.trial_days ?? defaultSettings.trial_days),
              default_plan: String(fullProduct.settings.default_plan ?? defaultSettings.default_plan),
            }
          : { ...defaultSettings };

        setFormData({
          code: fullProduct.code,
          name: fullProduct.name,
          description: fullProduct.description || '',
          plans: fullProduct.plans && fullProduct.plans.length > 0
            ? plansToFormData(fullProduct.plans)
            : [{ ...defaultPlanForm }],
          email_templates: fullProduct.email_templates && Object.keys(fullProduct.email_templates).length > 0
            ? fullProduct.email_templates
            : { ...defaultEmailTemplates },
          settings: productSettings,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'プロダクト詳細の取得に失敗しました');
        return;
      }
    } else {
      setEditingProduct(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        plans: [{ ...defaultPlanForm }],
        email_templates: { ...defaultEmailTemplates },
        settings: { ...defaultSettings },
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
  };

  // プラン操作
  const handlePlanChange = (index: number, field: keyof PlanFormData, value: string | number) => {
    const newPlans = [...formData.plans];
    const currentPlan = newPlans[index];
    if (!currentPlan) return;

    newPlans[index] = {
      code: field === 'code' ? (value as string) : currentPlan.code,
      name: field === 'name' ? (value as string) : currentPlan.name,
      max_devices: field === 'max_devices' ? (value as number) : currentPlan.max_devices,
      price: field === 'price' ? (value as number) : currentPlan.price,
      featuresText: field === 'featuresText' ? (value as string) : currentPlan.featuresText,
    };
    setFormData({ ...formData, plans: newPlans });
  };

  const handleAddPlan = () => {
    setFormData({
      ...formData,
      plans: [...formData.plans, { code: '', name: '', max_devices: 1, price: 0, featuresText: '' }],
    });
  };

  const handleRemovePlan = (index: number) => {
    if (formData.plans.length <= 1) return;
    const newPlans = formData.plans.filter((_, i) => i !== index);
    setFormData({ ...formData, plans: newPlans });
  };

  // メールテンプレート操作
  const handleEmailTemplateChange = (key: string, field: keyof EmailTemplate, value: string) => {
    const currentTemplate = formData.email_templates[key];
    if (!currentTemplate) return;

    setFormData({
      ...formData,
      email_templates: {
        ...formData.email_templates,
        [key]: {
          subject: field === 'subject' ? value : currentTemplate.subject,
          body: field === 'body' ? value : currentTemplate.body,
        },
      },
    });
  };

  const handleAddEmailTemplate = () => {
    const newKey = `template_${Object.keys(formData.email_templates).length + 1}`;
    setFormData({
      ...formData,
      email_templates: {
        ...formData.email_templates,
        [newKey]: { subject: '', body: '' },
      },
    });
  };

  const handleRemoveEmailTemplate = (key: string) => {
    if (Object.keys(formData.email_templates).length <= 1) return;
    const newTemplates = { ...formData.email_templates };
    delete newTemplates[key];
    setFormData({ ...formData, email_templates: newTemplates });
  };

  const handleRenameEmailTemplate = (oldKey: string, newKey: string) => {
    if (oldKey === newKey || !newKey.trim()) return;
    const template = formData.email_templates[oldKey];
    if (!template) return;

    const newTemplates: Record<string, EmailTemplate> = {};
    // キーの順序を保持しながら新しいキーに置き換え
    for (const [key, value] of Object.entries(formData.email_templates)) {
      if (key === oldKey) {
        newTemplates[newKey] = value;
      } else {
        newTemplates[key] = value;
      }
    }
    setFormData({ ...formData, email_templates: newTemplates });
  };

  // 設定操作
  const handleSettingsChange = (field: keyof Settings, value: boolean | number | string) => {
    setFormData({
      ...formData,
      settings: { ...formData.settings, [field]: value },
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description || null,
        plans: formDataToPlans(formData.plans),  // PlanFormData[] を Plan[] に変換
        email_templates: formData.email_templates,
        settings: formData.settings,
      };

      const url = editingProduct
        ? `${API_BASE}/products/${editingProduct.code}`
        : `${API_BASE}/products`;
      const method = editingProduct ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || '保存に失敗しました');
      }

      handleCloseDialog();
      fetchProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存エラー');
    }
  };

  const handleDelete = async (code: string) => {
    if (!confirm(`プロダクト「${code}」を削除しますか？`)) return;

    try {
      const res = await fetch(`${API_BASE}/products/${code}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || '削除に失敗しました');
      }

      fetchProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : '削除エラー');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">プロダクト管理</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProducts}
            disabled={loading}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            新規追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>コード</TableCell>
                <TableCell>名前</TableCell>
                <TableCell>プラン</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    プロダクトがありません
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {product.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {product.plans?.map((plan) => (
                          <Chip
                            key={plan.code}
                            label={`${plan.name} (¥${plan.price})`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.is_active ? '有効' : '無効'}
                        color={product.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="編集">
                        <IconButton onClick={() => handleOpenDialog(product)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton onClick={() => handleDelete(product.code)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'プロダクト編集' : '新規プロダクト追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* 基本情報 */}
            <TextField
              label="コード"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              disabled={!!editingProduct}
              helperText="例: new_product（英数字とアンダースコアのみ）"
              fullWidth
            />
            <TextField
              label="名前"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              helperText="例: 新商品名"
              fullWidth
            />
            <TextField
              label="説明"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />

            <Divider sx={{ my: 1 }} />

            {/* プラン設定 */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>プラン設定</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {formData.plans.map((plan, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2">プラン {index + 1}</Typography>
                      {formData.plans.length > 1 && (
                        <IconButton size="small" onClick={() => handleRemovePlan(index)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <TextField
                        label="コード"
                        value={plan.code}
                        onChange={(e) => handlePlanChange(index, 'code', e.target.value)}
                        size="small"
                        helperText="例: free, basic, pro"
                      />
                      <TextField
                        label="名前"
                        value={plan.name}
                        onChange={(e) => handlePlanChange(index, 'name', e.target.value)}
                        size="small"
                        helperText="例: 無料プラン"
                      />
                      <TextField
                        label="最大デバイス数"
                        type="number"
                        value={plan.max_devices}
                        onChange={(e) => handlePlanChange(index, 'max_devices', parseInt(e.target.value) || 1)}
                        size="small"
                      />
                      <TextField
                        label="価格（円）"
                        type="number"
                        value={plan.price}
                        onChange={(e) => handlePlanChange(index, 'price', parseInt(e.target.value) || 0)}
                        size="small"
                      />
                      <TextField
                        label="機能（カンマ区切り）"
                        value={plan.featuresText}
                        onChange={(e) => handlePlanChange(index, 'featuresText', e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ gridColumn: '1 / -1' }}
                        helperText="例: phase1-4, tool1-5, lp-generator"
                      />
                    </Box>
                  </Paper>
                ))}
                <Button startIcon={<AddIcon />} onClick={handleAddPlan} variant="outlined" size="small">
                  プランを追加
                </Button>
              </AccordionDetails>
            </Accordion>

            {/* メールテンプレート */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>メールテンプレート</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  利用可能な変数: {'{product_name}'}, {'{customer_name}'}, {'{license_key}'}
                </Typography>
                {Object.entries(formData.email_templates).map(([key, template]) => (
                  <Paper key={key} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <TextField
                        label="テンプレート名"
                        value={key}
                        onChange={(e) => handleRenameEmailTemplate(key, e.target.value)}
                        size="small"
                        sx={{ width: 200 }}
                        helperText="例: welcome, license_issued"
                      />
                      {Object.keys(formData.email_templates).length > 1 && (
                        <IconButton size="small" onClick={() => handleRemoveEmailTemplate(key)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <TextField
                      label="件名"
                      value={template.subject}
                      onChange={(e) => handleEmailTemplateChange(key, 'subject', e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="本文"
                      value={template.body}
                      onChange={(e) => handleEmailTemplateChange(key, 'body', e.target.value)}
                      multiline
                      rows={4}
                      fullWidth
                      size="small"
                    />
                  </Paper>
                ))}
                <Button startIcon={<AddIcon />} onClick={handleAddEmailTemplate} variant="outlined" size="small">
                  テンプレートを追加
                </Button>
              </AccordionDetails>
            </Accordion>

            {/* 設定 */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>設定</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.settings.require_device_auth}
                        onChange={(e) => handleSettingsChange('require_device_auth', e.target.checked)}
                      />
                    }
                    label="デバイス認証を必須にする"
                  />
                  <TextField
                    label="トライアル日数"
                    type="number"
                    value={formData.settings.trial_days}
                    onChange={(e) => handleSettingsChange('trial_days', parseInt(e.target.value) || 0)}
                    size="small"
                    sx={{ width: 200 }}
                  />
                  <TextField
                    label="デフォルトプラン"
                    value={formData.settings.default_plan}
                    onChange={(e) => handleSettingsChange('default_plan', e.target.value)}
                    size="small"
                    sx={{ width: 200 }}
                    helperText="上記プランのコードを入力"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
